import { prefersReducedMotion, mediaQueryLarge } from '@theme/utilities';

/* The drawer is a <dialog> whose visual state is driven by a single
 * registered CSS custom property `--drawer-progress` (0 = closed,
 * 1 = open). Both the dialog's transform and its ::backdrop opacity
 * derive from that one value, so there is only ever one thing to
 * coordinate.
 *
 * Open / close are class-driven: [open] sets --drawer-progress: 1,
 * .dialog-closing sets it back to 0, and a CSS transition on the
 * property interpolates between them. @starting-style provides the
 * `before` value for the open transition.
 *
 * This module owns the drag gesture: it writes inline `transition`
 * and `--drawer-progress` on the dialog. Inline always wins over the
 * class cascade, so the drag never fights CSS — when we want a hard
 * value we set inline, and when we're done we clear inline and let
 * the cascade resume. */

const DRAG_CLOSE_DISTANCE_PX = 100;
const DRAG_CLOSE_VELOCITY = 0.5; // px/ms
const DRAG_CLOSE_DURATION_MS = 220;
const DRAG_SNAP_BACK_DURATION_MS = 220;
const CLOSE_EASING = 'cubic-bezier(0.32, 0, 0.67, 1)';
const SNAP_BACK_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)';
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, label, summary, [role="button"], [role="link"]';
const BREAKPOINT = mediaQueryLarge;

export class BottomDrawer extends HTMLElement {
  #connected = false;
  #isMobile = false;
  #dialog = /** @type {HTMLDialogElement | null} */ (null);
  #dragStartY = 0;
  #dragStartTime = 0;
  #currentDelta = 0;
  #isDragging = false;
  #activeTransitionEnd = /** @type {((e: TransitionEvent) => void) | null} */ (null);

  connectedCallback() {
    if (this.#connected) return;
    this.#connected = true;

    this.#isMobile = !BREAKPOINT.matches;
    this.#dialog = /** @type {HTMLDialogElement | null} */ (this.closest('dialog'));

    if (this.#isMobile) this.#attachMobileHandlers();
    BREAKPOINT.addEventListener('change', this.#handleBreakpointChange);
    this.#dialog?.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    this.#connected = false;
    this.#detachMobileHandlers();
    BREAKPOINT.removeEventListener('change', this.#handleBreakpointChange);
    this.#dialog?.removeEventListener('close', this.#handleDialogClose);
  }

  open() {
    this.dispatchEvent(new CustomEvent('bottom-drawer:open', { bubbles: true }));
  }

  close() {
    this.dispatchEvent(new CustomEvent('bottom-drawer:close', { bubbles: true }));
  }

  #handleBreakpointChange = (event) => {
    const becameMobile = !event.matches;
    if (becameMobile === this.#isMobile) return;
    this.#isMobile = becameMobile;
    if (becameMobile) {
      this.#attachMobileHandlers();
    } else {
      this.#detachMobileHandlers();
      this.#cleanupDrag();
    }
  };

  #attachMobileHandlers() {
    this.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    this.addEventListener('touchend', this.#handleTouchEnd);
    this.addEventListener('touchcancel', this.#handleTouchEnd);
  }

  #detachMobileHandlers() {
    this.removeEventListener('touchstart', this.#handleTouchStart);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('touchend', this.#handleTouchEnd);
    this.removeEventListener('touchcancel', this.#handleTouchEnd);
  }

  #handleTouchStart = (event) => {
    if (!this.#isMobile || !this.#dialog) return;
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Touches on interactive controls (close button, rail tabs, links)
    // belong to that control — don't hijack them as a drag. Without
    // this, a tap on the close button starts a drag, finger jitter
    // generates a 1–5px deltaY, and the snap-back transition runs to
    // completion before closeDialog fires, making the drawer "jump
    // back to open" before finally closing.
    if (target.closest(INTERACTIVE_SELECTOR)) return;

    // The gesture belongs to whichever inner scroll container is
    // currently past its top edge — let it consume the swipe.
    let node = /** @type {HTMLElement | null} */ (target);
    while (node && node !== this) {
      if (node.scrollTop > 0) return;
      node = node.parentElement;
    }

    // If a drag-handle region is declared, restrict drag to touches
    // on that region (or the drawer surface itself, e.g. the ::before
    // pseudo-element drag indicator). Drawers without the attribute
    // keep the historical "drag from anywhere" behavior.
    const handle = this.querySelector('[data-drawer-handle]');
    if (handle && target !== this && !handle.contains(target)) return;

    this.#beginDrag(event);
  };

  #beginDrag(event) {
    if (!this.#dialog) return;
    const touch = event.touches[0];
    this.#dragStartY = touch.clientY;
    this.#dragStartTime = event.timeStamp;
    this.#currentDelta = 0;
    this.#isDragging = true;

    // If a previous drag-end transition is still mid-flight, drop its
    // listener before we take ownership again.
    this.#detachActiveTransitionEnd();

    // Pin --drawer-progress to its current open-state value while we
    // disable the transition. Setting `transition: none` first would
    // also work, but writing the value first ensures the dialog can't
    // ever observe the (computed-from-cascade) underlying value as a
    // separate state from what we want.
    this.#setProgress(1);
    this.#dialog.style.transition = 'none';
    this.#dialog.style.setProperty('will-change', 'transform');
  }

  #handleTouchMove = (event) => {
    if (!this.#isDragging || !this.#dialog) return;

    const touch = event.touches[0];
    const deltaY = touch.clientY - this.#dragStartY;

    if (deltaY <= 0) {
      this.#currentDelta = 0;
      this.#setProgress(1);
      return;
    }

    // Once we know the gesture is a downward drag, claim it so the
    // browser doesn't also try to scroll a parent.
    if (event.cancelable) event.preventDefault();

    this.#currentDelta = deltaY;
    const height = this.#dialog.offsetHeight || 1;
    this.#setProgress(Math.max(0, 1 - deltaY / height));
  };

  #handleTouchEnd = (event) => {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    if (!this.#dialog) return;

    const dialog = this.#dialog;
    const deltaY = this.#currentDelta;
    const duration = Math.max(1, event.timeStamp - this.#dragStartTime);
    const velocity = deltaY / duration;
    const reduced = prefersReducedMotion();
    const shouldClose = deltaY > DRAG_CLOSE_DISTANCE_PX || velocity > DRAG_CLOSE_VELOCITY;

    // Tap (no movement) — clean up and let the click handler win.
    // Should be unreachable now that touchstart bails on interactive
    // targets, but kept as a defense for any non-button drag origin.
    if (deltaY === 0) {
      this.#cleanupDrag();
      return;
    }

    if (reduced) {
      this.#cleanupDrag();
      if (shouldClose) this.close();
      return;
    }

    const targetProgress = shouldClose ? 0 : 1;
    const ms = shouldClose ? DRAG_CLOSE_DURATION_MS : DRAG_SNAP_BACK_DURATION_MS;
    const easing = shouldClose ? CLOSE_EASING : SNAP_BACK_EASING;

    // Drive the snap via a transition on --drawer-progress only — the
    // dialog's transform and the backdrop's opacity both derive from
    // it, so they animate in lockstep with no chance of one finishing
    // ahead of the other.
    dialog.style.transition = `--drawer-progress ${ms}ms ${easing}`;
    this.#setProgress(targetProgress);

    const onEnd = (e) => {
      if (e.propertyName !== '--drawer-progress') return;
      this.#detachActiveTransitionEnd();
      if (shouldClose) {
        // Hand off to closeDialog without clearing inline state. The
        // CSS rule for .dialog-closing also resolves --drawer-progress
        // to 0, and our inline value is already 0 — so closeDialog's
        // class change is a no-op for the visual state and its
        // `await onAnimationEnd` resolves immediately. dialog.close()
        // hides the dialog, then #handleDialogClose wipes inline.
        this.close();
      } else {
        this.#cleanupDrag();
      }
    };
    this.#activeTransitionEnd = onEnd;
    dialog.addEventListener('transitionend', onEnd);
  };

  #setProgress(value) {
    this.#dialog?.style.setProperty('--drawer-progress', String(value));
  }

  #detachActiveTransitionEnd() {
    if (!this.#activeTransitionEnd || !this.#dialog) return;
    this.#dialog.removeEventListener('transitionend', this.#activeTransitionEnd);
    this.#activeTransitionEnd = null;
  }

  /** Wipe all inline drag state. Called when a tap completes, when a
   * snap-back finishes, when the dialog is actually closed, or when
   * we leave the mobile breakpoint. */
  #cleanupDrag() {
    this.#isDragging = false;
    this.#currentDelta = 0;
    if (!this.#dialog) return;
    this.#detachActiveTransitionEnd();
    this.#dialog.style.removeProperty('transition');
    this.#dialog.style.removeProperty('--drawer-progress');
    this.#dialog.style.removeProperty('will-change');
  }

  #handleDialogClose = () => {
    this.#cleanupDrag();
  };
}

if (!customElements.get('bottom-drawer')) {
  customElements.define('bottom-drawer', BottomDrawer);
}
